import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Share2 } from "lucide-react";
import { toPng } from "html-to-image";

/**
 * Zola Rewards — Membership Card
 * Uses the finalised card artwork as-is (public/zola-card.png).
 * Only dynamic member data (name / ID / dates / status) is overlaid.
 * Standard credit-card orientation (ISO/IEC 7810 ID-1 · 1.586:1).
 */

function shortMonth(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear()).slice(-2)}`;
  } catch {
    return "—";
  }
}

export default function VirtualCard({ customer, compact }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  if (!customer) return null;

  const memberSince = shortMonth(customer.join_date);
  const balance = customer.reward_balance ?? 0;
  const status = (customer.status || "Active").toUpperCase();

  const download = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 3,
        cacheBust: true,
        backgroundColor: "#050f22",
      });
      const link = document.createElement("a");
      link.download = `zola-rewards-${customer.customer_id}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Card saved to your device");
    } catch (e) {
      toast.error("Could not save card. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    if (!cardRef.current) return;
    if (!navigator.canShare) return download();
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], `zola-rewards-${customer.customer_id}.png`, { type: "image/png" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Zola Rewards",
          text: `I'm a Zola Safari Tours rewards member ✈️`,
        });
      } else {
        download();
      }
    } catch {
      /* user cancelled */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={compact ? "" : "w-full"} data-testid="virtual-rewards-card-wrap">
      <div className="zola-card-frame">
        <div ref={cardRef} className="zola-card-v3" data-testid="virtual-rewards-card">
          <img
            src="/zola-card.png"
            alt=""
            className="zola-card-bg"
            crossOrigin="anonymous"
            aria-hidden
          />
          {/* Unified mask over the artwork's sample text zone */}
          <div className="zc3-mask" aria-hidden />
          {/* Dynamic overlays positioned to match the artwork */}
          <div className="zc3-name" data-testid="card-member-name">
            {(customer.name || "").toUpperCase()}
          </div>
          <div className="zc3-id" data-testid="card-customer-id">
            {customer.customer_id}
          </div>
          <div className="zc3-since" data-testid="card-member-since">
            {memberSince}
          </div>
          <div className="zc3-status" data-testid="card-status">
            {status}
          </div>
        </div>
      </div>

      {!compact && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="flex items-baseline gap-2 mr-auto">
            <span
              className="num-hero gold"
              style={{ lineHeight: 1 }}
              data-testid="card-balance"
            >
              {balance.toLocaleString()}
            </span>
            <span className="label-caps" style={{ letterSpacing: "0.22em" }}>
              PTS · ≈ AED {balance.toLocaleString()}
            </span>
          </div>
          <button
            type="button"
            onClick={download}
            disabled={busy}
            data-testid="card-download-btn"
            className="inline-flex items-center gap-2 rounded-full h-10 px-4 text-sm font-semibold"
            style={{
              background: "var(--navy)",
              color: "#fff",
              opacity: busy ? 0.7 : 1,
            }}
          >
            <Download className="h-4 w-4" />
            {busy ? "Saving…" : "Save to Wallet"}
          </button>
          <button
            type="button"
            onClick={share}
            disabled={busy}
            data-testid="card-share-btn"
            className="inline-flex items-center gap-2 rounded-full h-10 px-4 text-sm font-semibold"
            style={{
              background: "transparent",
              border: "1px solid var(--border-strong)",
              color: "var(--text)",
            }}
          >
            <Share2 className="h-4 w-4" />
            Share
          </button>
        </div>
      )}
    </div>
  );
}
